import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { LINK_KIND_LABELS, LINK_KINDS, textFragmentUrl, type LinkKind } from "@navibiz/chat-core";
import { api } from "../../lib/api";
import { keys, useAction } from "../../lib/queries";
import { Button } from "../../ui/Button";
import { Dialog } from "../../ui/Dialog";
import { useToast } from "../../ui/Feedback";
import { ChipsInput, Field, FormGrid, Input, Select, Switch, Textarea } from "../../ui/Form";

export interface LinkDraft {
  id?: string;
  kind: LinkKind;
  title: string;
  url: string;
  summary: string;
  price: string | null;
  imageUrl: string | null;
  tags: string[];
  active: boolean;
}

const HINTS: Record<LinkKind, string> = {
  page: "Satu halaman utuh: Tentang Kami, Kontak, Karier.",
  section: "Satu bagian halaman: tim pendiri di Tentang Kami, jam buka di Kontak. Pakai #id bagian itu, atau buat tautan ke teksnya di bawah.",
  product: "Produk atau layanan dengan halamannya sendiri. Harga dan foto ikut tampil di kartu.",
  article: "Artikel, studi kasus, atau proyek portofolio.",
  document: "PDF atau file yang bisa diunduh: daftar harga, company profile.",
};

/**
 * One referral. The title and summary are what the model reads to decide
 * whether to suggest it, so they should say plainly what is there; the tags
 * catch the words visitors use instead.
 */
export function LinkEditor({ projectId, websiteUrl, draft, onClose }: { projectId: string; websiteUrl: string | null; draft: LinkDraft | null; onClose: () => void }) {
  const toast = useToast();
  const [value, setValue] = useState<LinkDraft | null>(draft);
  const [phrase, setPhrase] = useState("");
  useEffect(() => {
    setValue(draft);
    setPhrase("");
  }, [draft]);

  const invalidate = [keys.links(projectId), keys.project(projectId)];
  const save = useAction((link: LinkDraft) => {
    const { id, ...body } = link;
    return id ? api.patch(`/projects/${projectId}/links/${id}`, body) : api.post(`/projects/${projectId}/links`, body);
  }, invalidate);
  const remove = useAction((id: string) => api.post(`/projects/${projectId}/links/bulk`, { action: "delete", ids: [id] }), invalidate);

  if (!draft || !value) return null;
  const update = (patch: Partial<LinkDraft>) => setValue({ ...value, ...patch });
  const valid = value.title.trim().length >= 2 && value.url.trim().length > 0;
  let fragment: string | null = null;
  try {
    fragment = phrase.trim() && value.url ? textFragmentUrl(new URL(value.url, websiteUrl ?? undefined).toString(), phrase) : null;
  } catch {
    fragment = null;
  }

  return (
    <Dialog
      open
      onClose={onClose}
      width={680}
      title={value.id ? "Ubah rujukan" : "Rujukan baru"}
      description="Langsung berlaku untuk jawaban berikutnya."
      footer={
        <>
          {value.id && (
            <Button
              variant="danger"
              icon={<Trash2 aria-hidden />}
              loading={remove.isPending}
              style={{ marginRight: "auto" }}
              onClick={async () => {
                if (!confirm("Hapus rujukan ini? Riwayat kliknya tetap ada di laporan.")) return;
                await remove.mutateAsync(value.id!);
                toast("Rujukan dihapus.");
                onClose();
              }}
            >
              Hapus
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={save.isPending}
            onClick={async () => {
              try {
                await save.mutateAsync({ ...value, price: value.price || null, imageUrl: value.imageUrl || null });
                toast(value.id ? "Rujukan diperbarui." : "Rujukan ditambahkan.");
                onClose();
              } catch (error) {
                toast(error instanceof Error ? error.message : "Gagal menyimpan.", "critical");
              }
            }}
          >
            Simpan
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 16 }}>
        <FormGrid>
          <Field label="Jenis" hint={HINTS[value.kind]}>
            {(id, describedBy) => (
              <Select id={id} value={value.kind} aria-describedby={describedBy} onChange={(event) => update({ kind: event.target.value as LinkKind })}>
                {LINK_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {LINK_KIND_LABELS[kind]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Judul di kartu">{(id) => <Input id={id} value={value.title} maxLength={160} autoFocus onChange={(event) => update({ title: event.target.value })} />}</Field>
        </FormGrid>
        <Field label="Alamat" hint={websiteUrl ? `Lengkap, atau path di ${websiteUrl.replace(/^https?:\/\//, "")} (mis. /tentang#tim).` : "Alamat lengkap dengan https://."}>
          {(id, describedBy) => <Input id={id} value={value.url} aria-describedby={describedBy} placeholder="https://…" onChange={(event) => update({ url: event.target.value })} />}
        </Field>
        {value.kind === "section" && (
          <Field label="Bagian tanpa #id? Tautkan ke teksnya" hint={fragment ? `Hasil: ${fragment}` : "Ketik kalimat pendek yang ada di bagian itu. Browser menggulir ke sana dan menyorotnya."}>
            {(id, describedBy) => (
              <div style={{ display: "flex", gap: 8 }}>
                <Input id={id} value={phrase} aria-describedby={describedBy} placeholder="mis. Tim pendiri kami" onChange={(event) => setPhrase(event.target.value)} />
                <Button disabled={!fragment} onClick={() => fragment && update({ url: fragment })}>
                  Pakai
                </Button>
              </div>
            )}
          </Field>
        )}
        <Field label="Ringkasan" hint="Satu-dua kalimat tentang isinya. Model membaca ini untuk memutuskan kapan menyarankannya; pengunjung melihatnya bila model tidak menulis alasan.">
          {(id, describedBy) => <Textarea id={id} rows={3} maxLength={300} value={value.summary} aria-describedby={describedBy} onChange={(event) => update({ summary: event.target.value })} />}
        </Field>
        <FormGrid>
          <Field label="Harga (opsional)" hint="Tampil di kartu. Kosongkan bila bisa berubah tanpa disinkron.">
            {(id, describedBy) => <Input id={id} value={value.price ?? ""} maxLength={60} placeholder="Rp 25.000" aria-describedby={describedBy} onChange={(event) => update({ price: event.target.value })} />}
          </Field>
          <Field label="Gambar (opsional)">{(id) => <Input id={id} value={value.imageUrl ?? ""} placeholder="https://…/foto.jpg" onChange={(event) => update({ imageUrl: event.target.value })} />}</Field>
        </FormGrid>
        <Field label="Tag" hint="Kata yang dipakai pengunjung untuk hal ini (mis. ongkir, founder, booking).">
          {(id) => <ChipsInput id={id} value={value.tags} max={12} onChange={(tags) => update({ tags })} placeholder="ketik lalu Enter" />}
        </Field>
        <Switch checked={value.active} onChange={(active) => update({ active })} label="Aktif" description="Rujukan nonaktif tidak pernah disarankan, tapi riwayatnya tetap tersimpan." />
      </div>
    </Dialog>
  );
}
